import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import DataExport from '../components/DataExport';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Truck, Package, AlertTriangle, Archive, 
  Calendar, CalendarDays, CalendarRange, CalendarClock,
  Download, FileText, FileSpreadsheet
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import api from '../utils/api';
import { toast } from 'sonner';

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, deliveryStatsRes, deliveriesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/delivery-stats'),
        api.get('/deliveries?period=today'),
      ]);
      setStats(statsRes.data);
      setDeliveryStats(deliveryStatsRes.data);
      setDeliveries(deliveriesRes.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (period, format) => {
    try {
      setExporting(true);
      
      if (format === 'csv') {
        const response = await api.get(`/dashboard/export-deliveries?period=${period}&format=csv`);
        const data = response.data;
        
        // Create and download CSV file
        const blob = new Blob([data.content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        toast.success(`Exported ${data.total_deliveries} deliveries to CSV`);
      } else if (format === 'pdf') {
        const response = await api.get(`/dashboard/export-deliveries?period=${period}&format=json`);
        const data = response.data;
        
        // Generate PDF using browser print
        generatePDF(data);
        toast.success(`Generated PDF report with ${data.total_deliveries} deliveries`);
      }
    } catch (error) {
      toast.error('Failed to export report');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const generatePDF = (data) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${data.period_label}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
          h1 { color: #1e3a5f; font-size: 18px; margin-bottom: 5px; }
          .meta { color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background-color: #1e3a5f; color: white; padding: 8px; text-align: left; font-size: 11px; }
          td { border: 1px solid #ddd; padding: 6px; font-size: 10px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .overweight { color: red; font-weight: bold; }
          .acceptable { color: green; }
          @media print {
            body { margin: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${data.period_label}</h1>
        <div class="meta">
          Generated: ${data.generated_at} | Total Deliveries: ${data.total_deliveries}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>Business Name</th>
              <th>Invoice</th>
              <th>Payload</th>
              <th>Weight</th>
              <th>Supervisor</th>
              <th>Driver</th>
              <th>Vehicle</th>
              <th>Pallets</th>
            </tr>
          </thead>
          <tbody>
            ${data.data.map(row => `
              <tr>
                <td>${row.date}</td>
                <td>${row.time}</td>
                <td>${row.business_name}</td>
                <td>${row.invoice_number}</td>
                <td class="${row.payload_status === 'OVERWEIGHT' ? 'overweight' : 'acceptable'}">${row.payload_status}</td>
                <td>${row.weight_kg}</td>
                <td>${row.supervisor_name}</td>
                <td>${row.driver_name}</td>
                <td>${row.vehicle_reg}</td>
                <td>${row.pallet_count}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-slate-600 mt-2">Overview of your fleet operations</p>
        </div>

        {/* Data Export Section - Only for Main Admin */}
        <DataExport user={user} />

        {/* Original Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Vehicles</CardTitle>
              <Truck className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900" data-testid="stat-total-vehicles">{stats?.total_vehicles || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Deliveries Today</CardTitle>
              <Package className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900" data-testid="stat-deliveries-today">{stats?.deliveries_today || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Overweight Deliveries</CardTitle>
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900" data-testid="stat-overweight-deliveries">{stats?.overweight_deliveries || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Outstanding Pallets</CardTitle>
              <Archive className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900" data-testid="stat-outstanding-pallets">{stats?.outstanding_pallets || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Delivery Statistics Section - Admin Only */}
        {isAdmin && deliveryStats && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Delivery Statistics - {deliveryStats.year}</h2>
                <p className="text-sm text-slate-500">Counts from January 1st, {deliveryStats.year}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Daily Deliveries */}
              <Card className="border-l-4 border-l-cyan-500 relative">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Daily Deliveries</CardTitle>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-5 w-5 text-cyan-500" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={exporting}>
                          <Download className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Export Daily Report</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleExport('daily', 'csv')}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('daily', 'pdf')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{deliveryStats.daily}</div>
                  <p className="text-xs text-slate-500 mt-1">Today: {deliveryStats.period_info?.today}</p>
                </CardContent>
              </Card>

              {/* Weekly Deliveries */}
              <Card className="border-l-4 border-l-indigo-500 relative">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Weekly Deliveries</CardTitle>
                  <div className="flex items-center gap-1">
                    <CalendarDays className="h-5 w-5 text-indigo-500" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={exporting}>
                          <Download className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Export Weekly Report</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleExport('weekly', 'csv')}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('weekly', 'pdf')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{deliveryStats.weekly}</div>
                  <p className="text-xs text-slate-500 mt-1">Week of: {deliveryStats.period_info?.week_start}</p>
                </CardContent>
              </Card>

              {/* Monthly Deliveries */}
              <Card className="border-l-4 border-l-purple-500 relative">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Monthly Deliveries</CardTitle>
                  <div className="flex items-center gap-1">
                    <CalendarRange className="h-5 w-5 text-purple-500" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={exporting}>
                          <Download className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Export Monthly Report</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleExport('monthly', 'csv')}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('monthly', 'pdf')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{deliveryStats.monthly}</div>
                  <p className="text-xs text-slate-500 mt-1">From: {deliveryStats.period_info?.month_start}</p>
                </CardContent>
              </Card>

              {/* Yearly Deliveries */}
              <Card className="border-l-4 border-l-pink-500 relative">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Yearly Deliveries</CardTitle>
                  <div className="flex items-center gap-1">
                    <CalendarClock className="h-5 w-5 text-pink-500" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={exporting}>
                          <Download className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Export Yearly Report</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleExport('yearly', 'csv')}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export as CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport('yearly', 'pdf')}>
                          <FileText className="h-4 w-4 mr-2" />
                          Export as PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{deliveryStats.yearly}</div>
                  <p className="text-xs text-slate-500 mt-1">From: {deliveryStats.period_info?.year_start}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Recent Deliveries */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Deliveries</CardTitle>
              <Link to="/deliveries">
                <Button variant="outline" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recent-deliveries-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Invoice</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Payload Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Driver</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-slate-500">No deliveries found</td>
                    </tr>
                  ) : (
                    deliveries.map((delivery) => (
                      <tr key={delivery.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4 text-sm">{delivery.invoice_number || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm">{delivery.customer_id ? 'Customer' : 'N/A'}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            delivery.status === 'Completed' ? 'bg-green-100 text-green-700' :
                            delivery.status === 'Waiting for Driver' ? 'bg-amber-100 text-amber-700' :
                            delivery.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                            delivery.status === 'Overweight' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {delivery.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              delivery.payload_status === 'ACCEPTABLE' ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-sm">{delivery.payload_status}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-sm ${
                            delivery.driver_name ? 'text-slate-900 font-medium' : 'text-slate-400'
                          }`}>
                            {delivery.driver_name || 'Pending'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Link to={`/deliveries/${delivery.id}`}>
                            <Button variant="ghost" size="sm">View</Button>
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
